import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { EvaluationDetails, EvaluationItem, EvaluationItemType } from '../../../../types/course';
import DashedPanel from './shared/DashedPanel';
import SubtypePickerStrip from './shared/SubtypePickerStrip';
import AddItemCTA from './shared/AddItemCTA';
import QuizManagementPanel from './QuizManagementPanel';
import { EVALUATION_REGISTRY, getEvaluationEntry } from './itemRegistry';
import { evaluationDetailsSchema } from './validation';
import { useDismissible } from '../../../../hooks/learner/useMemberConfig';
import { fetchCourse } from '../../../../services/courses';

interface EvaluationEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: Partial<EvaluationDetails> & Record<string, unknown>;
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: Record<string, unknown>) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
}

const REMINDER_STORAGE_KEY = 'quiz-editor-reminder-dismissed';
const REMINDER_COPY =
  'To mark a correct answer, simply select it. For multi-answer questions, select as many as apply.';

// The primary question types surfaced in the picker.
const PICKER_TYPES: ReadonlySet<EvaluationItemType> = new Set([
  'multiple_choice',
  'boolean_true_false',
  'tuple_matching',
  'completion_fill_blank',
]);

/** Heal legacy `single_choice` items into `multiple_choice`. The registry has
 *  no single-choice renderer (looking one up throws → black screen); the only
 *  producer was the removed "Multiple answers" toggle, so convert any stored
 *  stragglers back on load. */
function normalizeItems(raw: EvaluationItem[]): EvaluationItem[] {
  return raw.map((item) => {
    const legacy = item as unknown as {
      type: string;
      config?: { question?: string; options?: unknown[]; correct_answer?: string };
    };
    if (legacy.type !== 'single_choice') return item;
    return {
      ...item,
      type: 'multiple_choice',
      config: {
        question: legacy.config?.question ?? '',
        options: legacy.config?.options ?? [],
        correct_answers: legacy.config?.correct_answer ? [legacy.config.correct_answer] : [],
      },
    } as EvaluationItem;
  });
}

function normalizeDetails(raw: Partial<EvaluationDetails> | undefined): EvaluationDetails {
  return {
    items: Array.isArray(raw?.items) ? normalizeItems(raw!.items as EvaluationItem[]) : [],
    passing_grade_percent:
      typeof raw?.passing_grade_percent === 'number' ? raw!.passing_grade_percent : null,
    max_attempts: typeof raw?.max_attempts === 'number' ? raw!.max_attempts : null,
    shuffle_items: !!raw?.shuffle_items,
    is_main_quiz: !!raw?.is_main_quiz,
  };
}

export default function EvaluationEditor({ module, courseId, onUpdate }: EvaluationEditorProps) {
  const [details, setDetails] = useState<EvaluationDetails>(() => normalizeDetails(module.details));
  const [pickerOpen, setPickerOpen] = useState(false);

  // Checked ON DEMAND when the trainer flips the main-quiz toggle: a fresh
  // course fetch (not the wizard's query cache, which module autosaves don't
  // invalidate) tells us whether a DIFFERENT quiz already holds the flag.
  const findMainQuizConflict = useCallback(async () => {
    if (!courseId) return null;
    try {
      const course = await fetchCourse(courseId);
      for (const section of course.sections ?? []) {
        for (const sibling of section.modules ?? []) {
          if (
            sibling.id !== module.id &&
            sibling.type === 'evaluation' &&
            (sibling.details as { is_main_quiz?: boolean } | undefined)?.is_main_quiz
          ) {
            return { id: sibling.id, title: sibling.title };
          }
        }
      }
    } catch {
      // Fresh check failed — proceed without the confirmation; the backend
      // still enforces a single main quiz on save.
    }
    return null;
  }, [courseId, module.id]);
  const {
    dismissed: reminderDismissed,
    isLoading: reminderLoading,
    dismiss: dismissReminder,
  } = useDismissible(REMINDER_STORAGE_KEY);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<EvaluationDetails | null>(null);
  const lastModuleIdRef = useRef(module.id);
  // The parent recreates onUpdate every render; going through a ref keeps the
  // flush-on-unmount effect from re-running (and double-saving) per render.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (module.id !== lastModuleIdRef.current) {
      lastModuleIdRef.current = module.id;
      setDetails(normalizeDetails(module.details));
      return;
    }
    // Same module, refreshed data — e.g. reopening the overlay while the
    // invalidated modules query was still refetching. Adopt the incoming
    // details only when no local edit is pending, so fresh server truth wins
    // without ever clobbering in-flight typing.
    if (saveTimeoutRef.current === null) {
      const incoming = normalizeDetails(module.details);
      setDetails((prev) =>
        JSON.stringify(prev) === JSON.stringify(incoming) ? prev : incoming,
      );
    }
  }, [module.id, module.details]);

  const persist = useCallback(
    (next: EvaluationDetails) => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      pendingSaveRef.current = next;
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null; // fired — no edit pending any more
        pendingSaveRef.current = null;
        const result = evaluationDetailsSchema.safeParse(next);
        if (!result.success) {
          // Skip the autosave while the aggregate is still incomplete —
          // the next edit will re-trigger this check once the form is valid.
          return;
        }
        void onUpdateRef.current(module.id, { details: result.data });
      }, 1500);
    },
    [module.id],
  );

  // Flush (not discard) a debounced save when the editor unmounts or switches
  // modules — otherwise "toggle a setting and close the overlay within 1.5s"
  // silently loses the change. Depends on module.id ONLY: re-running per
  // render would fire duplicate PUTs that can race each other.
  useEffect(() => {
    const moduleId = module.id;
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (pending) {
        const result = evaluationDetailsSchema.safeParse(pending);
        if (result.success) {
          void onUpdateRef.current(moduleId, { details: result.data });
        }
      }
    };
  }, [module.id]);

  const updateDetails = useCallback(
    (patch: Partial<EvaluationDetails>) => {
      setDetails((prev) => {
        const next = { ...prev, ...patch };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const addItem = useCallback(
    (type: EvaluationItemType) => {
      setDetails((prev) => {
        const entry = getEvaluationEntry(type);
        const newItem = entry.defaultItem(prev.items.length);
        const next = { ...prev, items: [...prev.items, newItem] };
        persist(next);
        return next;
      });
      setPickerOpen(false);
    },
    [persist],
  );

  const updateItem = useCallback(
    (id: string, patch: Partial<EvaluationItem>) => {
      setDetails((prev) => {
        const next = {
          ...prev,
          items: prev.items.map((it) => (it.id === id ? ({ ...it, ...patch } as EvaluationItem) : it)),
        };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const deleteItem = useCallback(
    (id: string) => {
      setDetails((prev) => {
        if (prev.items.length <= 1) return prev;
        const remaining = prev.items.filter((it) => it.id !== id);
        const next = {
          ...prev,
          items: remaining.map((it, idx) => ({ ...it, order_index: idx })) as EvaluationItem[],
        };
        persist(next);
        return next;
      });
    },
    [persist],
  );


  const sortedItems = useMemo(
    () => [...details.items].sort((a, b) => a.order_index - b.order_index),
    [details.items],
  );

  return (
    <div className="flex flex-col gap-7 w-full max-w-[920px] mx-auto">
      <DashedPanel>
        <h2 className="mt-0 mb-5 text-center font-sans font-semibold text-lg leading-7 text-text-primary">
          Create Quiz
        </h2>

        {!reminderLoading && !reminderDismissed && (
          <ReminderBanner message={REMINDER_COPY} onDismiss={dismissReminder} />
        )}

        <div className="mt-6 flex flex-col gap-8">
          <AnimatePresence initial={false}>
            {sortedItems.map((item) => {
              const entry = getEvaluationEntry(item.type);
              const Component = entry.Component;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                >
                  <Component
                    item={item}
                    onChange={(patch: Partial<EvaluationItem>) => updateItem(item.id, patch)}
                    onDelete={() => deleteItem(item.id)}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>

          {sortedItems.length === 0 && !pickerOpen && (
            <p className="text-center py-6 font-sans text-[13px] text-text-secondary">
              Add your first question to get started.
            </p>
          )}

          <div className={sortedItems.length > 0 ? 'border-t border-border-primary/15 pt-7 mt-1' : ''}>
          <AnimatePresence initial={false} mode="wait">
            {pickerOpen ? (
              <motion.div
                key="picker"
                initial={{ opacity: 0, height: 0, y: -4 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -4 }}
                transition={{
                  height: { duration: 0.24, ease: [0.23, 1, 0.32, 1] },
                  opacity: { duration: 0.22, ease: [0.23, 1, 0.32, 1] },
                  y: { duration: 0.22, ease: [0.23, 1, 0.32, 1] },
                }}
                className="overflow-hidden origin-top"
              >
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setPickerOpen(false)}
                      aria-label="Close question picker"
                      className="flex items-center justify-center w-7 h-7 rounded-full text-text-secondary border-0 bg-transparent cursor-pointer transition-[color,background-color,transform] duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] hover:text-text-primary hover:bg-text-secondary/10 active:scale-[0.92]"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <SubtypePickerStrip
                    subtypes={EVALUATION_REGISTRY.filter((entry) =>
                      PICKER_TYPES.has(entry.type),
                    ).map(({ type, label, Icon }) => ({
                      type,
                      label,
                      Icon,
                    }))}
                    onPick={(type) => addItem(type as EvaluationItemType)}
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="add-more"
                initial={{ opacity: 0, y: 10, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.98 }}
                transition={{
                  duration: 0.28,
                  ease: [0.23, 1, 0.32, 1],
                  opacity: { duration: 0.22, ease: [0.23, 1, 0.32, 1] },
                }}
                className="origin-top flex justify-center"
              >
                <AddItemCTA label="Add a New Question" onClick={() => setPickerOpen(true)} />
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        </div>
      </DashedPanel>

      <QuizManagementPanel
        details={details}
        onChange={updateDetails}
        onCheckMainQuizConflict={findMainQuizConflict}
      />
    </div>
  );
}

// ─── Inline reminder banner ─────────────────────────────────────────────

interface ReminderBannerProps {
  message: string;
  onDismiss: () => void;
}

function ReminderBanner({ message, onDismiss }: ReminderBannerProps) {
  return (
    <div
      role="note"
      className="flex items-start gap-3 rounded-2xl px-3.5 py-3 bg-secondary-500/10 border border-secondary-500/40"
    >
      <span
        aria-hidden="true"
        className="flex-shrink-0 mt-[1px] flex items-center justify-center w-[22px] h-[22px] rounded-full bg-secondary-500 font-sans font-bold text-[13px]"
      >
        i
      </span>
      <p className="flex-1 m-0 font-sans text-[13px] leading-5 text-text-primary">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss reminder"
        className="flex-shrink-0 flex items-center justify-center w-[22px] h-[22px] p-0 border-0 bg-transparent text-secondary-500 cursor-pointer"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
